import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Loader2, Plus, Trash2, Eye, EyeOff, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { manageHouseAds, createHouseAd, updateHouseAd, deleteHouseAd, uploadImage, mediaUrl } from "@/lib/api";
import { invalidateHouseAds } from "@/components/AdSlot";

const PLACEMENTS = [
  { id: "all", label: "All slots" },
  { id: "leaderboard", label: "Leaderboard (wide)" },
  { id: "rectangle", label: "Rectangle (sidebar)" },
  { id: "infeed", label: "In-feed (category)" },
];

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", link_url: "", placement: "all", image: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    manageHouseAds().then(setAds).catch(() => toast.error("Failed to load ads")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      set("image", url);
      toast.success("Banner uploaded.");
    } catch {
      toast.error("Upload failed (max 8MB).");
    } finally {
      setUploading(false);
    }
  };

  const create = async () => {
    if (!form.title.trim() || !form.link_url.trim() || !form.image) {
      toast.error("Title, destination link and a banner image are required.");
      return;
    }
    setSaving(true);
    try {
      await createHouseAd(form);
      invalidateHouseAds();
      toast.success("Ad is live in its slots.");
      setForm({ title: "", link_url: "", placement: "all", image: null });
      load();
    } catch {
      toast.error("Could not create ad.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (ad) => {
    try {
      await updateHouseAd(ad.id, !ad.active);
      invalidateHouseAds();
      setAds((a) => a.map((x) => (x.id === ad.id ? { ...x, active: !x.active } : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await deleteHouseAd(id);
      invalidateHouseAds();
      setAds((a) => a.filter((x) => x.id !== id));
      toast.success("Ad removed.");
    } catch {
      toast.error("Delete failed");
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-16">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
      </Link>
      <div className="mt-4">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Internal · Ad Manager</span>
        <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="admin-ads-title">
          House Ads
        </h1>
        <p className="text-muted-foreground text-lg mt-4 max-w-3xl">
          Run your own ads now — no AdSense needed. Upload a banner, point it to any URL and it
          fills the ad slots across the site instantly. Once your Google AdSense is approved,
          just deactivate house ads and AdSense takes over automatically.
        </p>
      </div>

      {/* Create form */}
      <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
        <div className="font-anton text-xl uppercase tracking-tight">New Ad</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={label}>Ad title / advertiser</label>
            <input data-testid="ad-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Atelier Noir — Spring Drop" className={inputCls} />
          </div>
          <div>
            <label className={label}>Destination URL</label>
            <input data-testid="ad-link" value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="https://…" className={inputCls} />
          </div>
        </div>
        <div>
          <label className={label}>Where should it show?</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PLACEMENTS.map((p) => (
              <button
                key={p.id}
                data-testid={`ad-placement-${p.id}`}
                onClick={() => set("placement", p.id)}
                className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  form.placement === p.id ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={label}>Banner image</label>
          {form.image ? (
            <div className="mt-2 relative w-full max-w-md">
              <img src={mediaUrl(form.image)} alt="Banner" className="w-full h-32 object-cover border border-white/15" />
              <button onClick={() => set("image", null)} className="absolute top-2 right-2 bg-black/70 text-white p-1 hover:bg-signal transition-colors" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="mt-2 flex items-center gap-2 w-fit cursor-pointer font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2.5 hover:border-signal hover:text-signal transition-colors" data-testid="ad-upload-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Upload banner"}
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="ad-file-input" />
            </label>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 mt-2">
            Tip: wide banners (~970×250 or 728×90) look best in leaderboards.
          </p>
        </div>
        <button
          data-testid="ad-create-btn"
          onClick={create}
          disabled={saving || !form.image || !form.title.trim() || !form.link_url.trim()}
          className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Publish ad
        </button>
      </div>

      {/* List */}
      <h2 className="font-anton text-2xl uppercase tracking-tight mt-14 mb-4">Current ads</h2>
      {loading ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : ads.length === 0 ? (
        <div className="border border-white/10 bg-card p-10 text-center" data-testid="admin-ads-empty">
          <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="font-mono text-sm text-muted-foreground mt-3">No house ads yet. Create one above and it appears in the ad slots immediately.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ads.map((ad) => (
            <div key={ad.id} data-testid={`admin-ad-${ad.id}`} className={`border bg-card ${ad.active ? "border-white/10" : "border-white/10 opacity-60"}`}>
              <div className="relative aspect-[16/6] overflow-hidden bg-background">
                <img src={mediaUrl(ad.image)} alt={ad.title} className="w-full h-full object-cover" />
                <span className={`absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 ${ad.active ? "bg-rankup text-black" : "bg-white/20 text-white"}`}>
                  {ad.active ? "Live" : "Paused"}
                </span>
              </div>
              <div className="p-4">
                <div className="font-display text-lg font-semibold leading-tight">{ad.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1 truncate">{ad.placement} · {ad.link_url}</div>
                <div className="flex items-center gap-2 mt-3">
                  <button data-testid={`admin-ad-toggle-${ad.id}`} onClick={() => toggle(ad)} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border border-white/15 px-3 py-1.5 hover:border-signal hover:text-signal transition-colors">
                    {ad.active ? <><EyeOff className="w-3.5 h-3.5" /> Pause</> : <><Eye className="w-3.5 h-3.5" /> Activate</>}
                  </button>
                  <button data-testid={`admin-ad-delete-${ad.id}`} onClick={() => remove(ad.id)} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border border-white/15 px-3 py-1.5 text-muted-foreground hover:border-signal hover:text-signal transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
